import { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { logger } from '../utils/logger';
import { reportError } from '../utils/errorReporting';
import { attemptChunkRecovery, isChunkLoadError } from '../utils/lazyWithChunkReload';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
        errorInfo: null,
    };

    public static getDerivedStateFromError(error: Error): State {
        return {
            hasError: true,
            error,
            errorInfo: null,
        };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        if (isChunkLoadError(error) && attemptChunkRecovery('app-shell')) {
            logger.warn('Chunk load failed, reloading app with cache bust.', error);
            return;
        }

        logger.error('Uncaught error:', error, errorInfo);
        reportError(error, { componentStack: errorInfo.componentStack });
        this.setState({
            error,
            errorInfo,
        });
    }

    private handleRefresh = () => {
        window.location.reload();
    };

    public render() {
        if (this.state.hasError) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100vh',
                        p: 3,
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="h4" gutterBottom color="error">
                        Something went wrong
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        We're sorry, but there was an error loading this page.
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<RefreshIcon />}
                        onClick={this.handleRefresh}
                    >
                        Refresh Page
                    </Button>
                    {process.env.NODE_ENV === 'development' && (
                        <Box sx={{ mt: 4, textAlign: 'left', width: 'min(960px, 100%)' }}>
                            <Typography variant="h6" gutterBottom>
                                Error Details:
                            </Typography>
                            <Box
                                component="pre"
                                sx={{
                                    m: 0,
                                    whiteSpace: 'pre-wrap',
                                    backgroundColor: 'transparent',
                                    color: 'text.primary',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    p: 2,
                                    borderRadius: 1,
                                    maxWidth: '100%',
                                    overflow: 'auto',
                                }}
                            >
                                {this.state.error?.toString()}
                                {'\n\n'}
                                {this.state.errorInfo?.componentStack}
                            </Box>
                        </Box>
                    )}
                </Box>
            );
        }

        return this.props.children;
    }
} 